<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <!-- Anchor -->
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaAvatar :size="32" color="primary" font-size="0.65rem" :title="store.fullName ?? ''">{{
              initials
            }}</VaAvatar>
          </span>
        </VaButton>
      </template>

      <!-- Dropdown Content -->
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <!-- Header Group -->
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4 mb-2">
            {{ t(`user.${group.name}`) }}
          </header>

          <!-- Items -->
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>

          <!-- Separator -->
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>

  <ResetPasswordModal v-if="isChangePasswordOpen" @cancel="isChangePasswordOpen = false" />
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import ResetPasswordModal from '@/pages/preferences/modals/ResetPasswordModal.vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useGlobalStore } from '@/stores/global-store'

const router = useRouter()
const { t } = useI18n()
const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const isShown = ref(false)
const isChangePasswordOpen = ref(false)

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

// Props + default options
withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          { name: 'profile', to: 'preferences', icon: 'mso-account_circle' },
          { name: 'change-password', icon: 'mso-key' },
        ],
      },
      {
        name: '',
        separator: false,
        list: [{ name: 'logout', to: 'login', icon: 'mso-logout' }],
      },
    ],
  },
)

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const store = useGlobalStore()

const initials = computed(() => {
  const name = store.fullName ?? ''
  return name
    .split(' ')
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0].toUpperCase())
    .join('')
})

// Click handler
const handleClick = (item: ProfileListItem) => {
  isShown.value = false

  if (item.name === 'logout') {
    store.logout()
    if (item.to) router.push({ name: item.to })
  } else if (item.name === 'change-password') {
    isChangePasswordOpen.value = true
  } else {
    if (item.to) router.push({ name: item.to })
    else if (item.href) window.open(item.href, '_blank')
  }
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
